import type { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { Database } from "@/utils/supabase.types";
import type { Annotation, Canvas, Storyboard, SubCanvas } from "@/utils/types";
import {
  type StoryboardDelta,
  UpdateStoryboardsChange,
} from "./zustand/changeLedgerTypes";
import { useDesignCommandManager } from "./zustand/designCommandManagerStore";
import { useIssuesStore } from "./zustand/issues-store";
import { useSupabaseStore } from "./zustand/supabaseStore";
import { getUpdateClassChangeForDeltas } from "@/components/RightStyle/TailwindStylingPanel/utils/updateStoryboardUtils";

// Type aliases from Database schema
export type ElementMetadata =
  Database["public"]["Tables"]["element_metadata"]["Row"];
type SubCanvasRow = Database["public"]["Tables"]["sub_canvases"]["Row"];
type ProjectRow = Database["public"]["Tables"]["projects"]["Row"];
type ActivityStreamDiff =
  Database["public"]["Tables"]["activity_stream_diffs"]["Row"];
type ProjectLoadingProgress =
  Database["public"]["Tables"]["project_loading_progress"]["Row"];

// State interface
interface SupabaseState {
  // Diff's are looked up based on the activity ID (equal to ID)
  activityStreamDiffs: Record<string, ActivityStreamDiff>;
  activityStreamDiffsLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  // Project loading statuses
  projectLoadingProgress: ProjectLoadingProgress | null;
  projectLoadingProgressLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  projectIdsByLastUpdated: Array<{ id: string; most_recent_update: string }>;
  projectIdsByLastUpdatedLoadStatus:
    | "idle"
    | "loading"
    | "succeeded"
    | "failed";
  projects: Record<string, ProjectRow>;
  projectsLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  ignoreCanvasUpdates: boolean;

  subCanvases: Record<string, SubCanvasRow>;
  subCanvasesLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  elementMetadata: Record<string, ElementMetadata>;
  elementMetadataLoadStatus: "idle" | "loading" | "succeeded" | "failed";

  // attached figma context by canvas id
  figmaContext: Record<string, unknown>;
  dsPrompt: string;
}

// Action payload interfaces
interface DSPromptPayload {
  prompt?: string;
}

interface UpdateCanvasFieldsPayload {
  id: string;
  fields: Record<string, unknown>;
}

interface SnapshotPayload<T> {
  newItems?: T[];
  deletedItems?: T[];
  forceUpdate?: boolean;
}

const initialState: SupabaseState = {
  // Diff's are looked up based on the activity ID (equal to ID)
  activityStreamDiffs: {},
  activityStreamDiffsLoadStatus: "idle",

  // Project loading statuses
  projectLoadingProgress: null,
  projectLoadingProgressLoadStatus: "idle",

  projectIdsByLastUpdated: localStorage.getItem("projectIdsByLastUpdated")
    ? JSON.parse(localStorage.getItem("projectIdsByLastUpdated") || "[]")
    : [],
  projectIdsByLastUpdatedLoadStatus: "idle",
  projects: {},
  projectsLoadStatus: "idle",

  ignoreCanvasUpdates: false,

  subCanvases: {},
  subCanvasesLoadStatus: "idle",

  elementMetadata: {},
  elementMetadataLoadStatus: "idle",

  // attached figma context by canvas id
  figmaContext: {},
  dsPrompt: "",
};

export const supabaseSlice = createSlice({
  name: "supabaseStore",
  initialState,

  reducers: {
    setDSPrompt: (state, action: PayloadAction<DSPromptPayload>) => {
      if (action.payload?.prompt) {
        state.dsPrompt = action.payload.prompt;
      } else {
        state.dsPrompt = "";
      }
    },
    onDSPromptSnapshot: (state, action: PayloadAction<string>) => {
      state.dsPrompt = "";
      state.dsPrompt = action.payload || "";
    },
    setFigmaContext: (
      state,
      action: PayloadAction<Record<string, unknown> | null>,
    ) => {
      state.figmaContext = action.payload || {};
    },
    onFigmaContextSnapshot: (
      state,
      action: PayloadAction<Record<string, unknown> | null>,
    ) => {
      state.figmaContext = {};
      state.figmaContext = action.payload || {};
    },
    setElementMetadata: (state, action: PayloadAction<ElementMetadata[]>) => {
      state.elementMetadata = {};

      if (action.payload) {
        for (const metadata of action.payload) {
          (state.elementMetadata as Record<string, ElementMetadata>)[
            metadata.hash
          ] = metadata;
        }
      }
      state.elementMetadataLoadStatus = "succeeded";
    },
    onElementMetadataSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<ElementMetadata>>,
    ) => {
      if (action.payload?.newItems) {
        for (const metadata of action.payload.newItems) {
          (state.elementMetadata as Record<string, ElementMetadata>)[
            metadata.hash
          ] = metadata;
        }
      }

      if (action.payload?.deletedItems) {
        for (const metadata of action.payload.deletedItems) {
          delete state.elementMetadata[metadata.hash];
        }
      }
    },
    setElementMetadataLoading: (state) => {
      state.elementMetadataLoadStatus = "loading";
    },
    setIgnoreCanvasUpdates: (state, action: PayloadAction<boolean>) => {
      state.ignoreCanvasUpdates = action.payload;
    },
    setProjectIdsByLastUpdated: (
      state,
      action: PayloadAction<Array<{ id: string; most_recent_update: string }>>,
    ) => {
      localStorage.setItem(
        "projectIdsByLastUpdated",
        JSON.stringify(action.payload),
      );
      state.projectIdsByLastUpdated = action.payload;
      state.projectIdsByLastUpdatedLoadStatus = "succeeded";
    },
    setProjectIdsByLastUpdatedLoading: (state) => {
      state.projectIdsByLastUpdatedLoadStatus = "loading";
    },
    setProjects: (state, action: PayloadAction<ProjectRow[]>) => {
      state.projects = {};
      if (action.payload) {
        for (const project of action.payload) {
          // @ts-expect-error
          state.projects[project.id] = project;
        }
      }
      state.projectsLoadStatus = "succeeded";
    },
    onProjectsSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<ProjectRow>>,
    ) => {
      const newProjects = { ...(state.projects || {}) };
      if (action.payload?.newItems) {
        for (const project of action.payload.newItems) {
          newProjects[project.id] = project;
        }
      }

      if (action.payload?.deletedItems) {
        for (const project of action.payload.deletedItems) {
          delete newProjects[project.id];
        }
      }

      state.projects = newProjects;
    },
    setProjectsLoading: (state) => {
      state.projectsLoadStatus = "loading";
    },

    setActivityStreamDiffs: (
      state,
      action: PayloadAction<ActivityStreamDiff[]>,
    ) => {
      // we don't reset activityStreamDiffs to {} because they are immutable - new ones are just appended to the state
      if (action.payload) {
        for (const diff of action.payload) {
          state.activityStreamDiffs[diff.id] = diff;
        }
      }
      state.activityStreamDiffsLoadStatus = "succeeded";
    },
    onActivityStreamDiffsSnapshot: (
      state,
      action: PayloadAction<SnapshotPayload<ActivityStreamDiff>>,
    ) => {
      if (action.payload?.newItems) {
        for (const diff of action.payload.newItems) {
          state.activityStreamDiffs[diff.id] = diff;
        }
      }

      if (action.payload?.deletedItems) {
        for (const diff of action.payload.deletedItems) {
          delete state.activityStreamDiffs[diff.id];
        }
      }
    },
    setActivityStreamDiffsLoading: (state) => {
      state.activityStreamDiffsLoadStatus = "loading";
    },

    setProjectLoadingProgress: (
      state,
      action: PayloadAction<ProjectLoadingProgress>,
    ) => {
      state.projectLoadingProgress = action.payload;
      state.projectLoadingProgressLoadStatus = "succeeded";
    },
    setProjectLoadingProgressLoading: (state) => {
      state.projectLoadingProgressLoadStatus = "loading";
    },
    onProjectLoadingProgressSnapshot: (
      state,
      action: PayloadAction<ProjectLoadingProgress>,
    ) => {
      state.projectLoadingProgress = action.payload;
    },
  },
});

/**
 * Utility to update storyboards & annotations & set in redux instantly
 */
export const updateCanvasStoryboards = (
  canvasId: string,
  storyboardDeltas: StoryboardDelta[],
  valuesAtStartOfAction?: Record<string, Partial<Storyboard>>,
  addToLedger?: boolean,
) => {
  const validDeltas = storyboardDeltas.filter(
    (delta) => delta.storyboardId || delta.changeType === "remove",
  );

  if (!validDeltas?.length) {
    return;
  }

  const change = new UpdateStoryboardsChange({
    canvasId,
    deltas: validDeltas,
    additionalGroupedChange: getUpdateClassChangeForDeltas(
      canvasId,
      validDeltas,
    ),
    valuesAtStartOfAction,
  });

  if (addToLedger) {
    useDesignCommandManager.getState().executeCommand(change);
  } else {
    useDesignCommandManager.getState().sendInstantChange(change);
  }
};

export const updateCanvasAnnotations = async (
  dispatch: Dispatch<AnyAction>,
  annotations: Annotation[],
  canvas: Canvas | SubCanvas,
  updateTs?: Date,
) => {
  const supabaseClient = useSupabaseStore.getState().supabaseClient;
  if (!supabaseClient) {
    console.error("Supabase client not available");
    return;
  }

  const last_updated = updateTs || new Date();

  const updateDict: Record<string, unknown> = {
    last_updated: last_updated.toISOString(),
    annotations: annotations,
  };

  // This is a sub-canvas
  if ((canvas as SubCanvas).canvas_id) {
    const newItem = {
      ...canvas,
      ...updateDict,
    };
    useSupabaseStore.getState().onDbSnapshot("subCanvases", {
      newItems: [newItem as unknown as SubCanvasRow],
      forceUpdate: true,
    });

    await supabaseClient
      .from("sub_canvases")
      .update(updateDict)
      .eq("id", canvas.id);

    return;
  }

  await useIssuesStore
    .getState()
    .updateCanvas(supabaseClient, canvas.id, updateDict);
};

export const {
  setElementMetadata,
  onElementMetadataSnapshot,
  setElementMetadataLoading,
  setProjects,
  onProjectsSnapshot,
  setProjectsLoading,
  setIgnoreCanvasUpdates,
  setActivityStreamDiffs,
  setActivityStreamDiffsLoading,
  onActivityStreamDiffsSnapshot,
  setProjectLoadingProgress,
  onProjectLoadingProgressSnapshot,
  setProjectLoadingProgressLoading,

  setProjectIdsByLastUpdated,
  setProjectIdsByLastUpdatedLoading,
  setFigmaContext,
  onFigmaContextSnapshot,

  setDSPrompt,
  onDSPromptSnapshot,
} = supabaseSlice.actions;

export default supabaseSlice.reducer;
